import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ChevronLeft, ChevronRight, ShieldCheck, ShieldAlert, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import BackupStatusBadge from "../components/BackupStatusBadge";

const PAGE_SIZE = 25;
const ENTITY_TYPES = ["case", "team_member", "document", "user", "settings", "access_review"];
const ACTIONS = ["create", "update", "upload", "status_change", "login"];

function shortUserAgent(ua) {
  if (!ua) return "—";
  const s = ua.toLowerCase();
  if (s.includes("edg")) return "Edge";
  if (s.includes("chrome") && !s.includes("opr")) return "Chrome";
  if (s.includes("firefox")) return "Firefox";
  if (s.includes("safari") && !s.includes("chrome")) return "Safari";
  if (s.includes("curl")) return "curl";
  if (s.includes("python") || s.includes("httpx") || s.includes("requests")) return "API client";
  return ua.length > 24 ? ua.slice(0, 22) + "…" : ua;
}

function relativeAgo(ts) {
  if (!ts) return "never";
  const d = new Date(ts);
  const diff = Math.max(0, Date.now() - d.getTime());
  if (diff < 60_000) return "just now";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)} min ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)} hr ago`;
  return d.toLocaleString();
}

function IntegrityBadge() {
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    try {
      const { data } = await api.get("/audit-logs/integrity-status");
      setStatus(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const verifyNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/audit-logs/verify-integrity");
      if (data.ok) {
        toast.success(`Integrity verified · ${data.total_verified.toLocaleString()} entries`);
      } else {
        toast.error(`Integrity check FAILED at chain index ${data.broken_at} (${data.reason || "mismatch"})`);
      }
      await refresh();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  if (!status) return null;
  const verified_ok = status.last_verified_ok !== false;

  return (
    <div
      data-testid="audit-integrity-badge"
      className="flex flex-wrap items-center gap-3 rounded-lg px-4 py-3"
      style={{
        backgroundColor: verified_ok ? "var(--success-bg)" : "var(--danger-bg)",
        border: `1px solid ${verified_ok ? "var(--success-border)" : "var(--danger-border)"}`,
      }}
    >
      {verified_ok ? (
        <ShieldCheck
          className="h-5 w-5"
          strokeWidth={1.7}
          style={{ color: "var(--success-fg)" }}
        />
      ) : (
        <ShieldAlert
          className="h-5 w-5"
          strokeWidth={1.7}
          style={{ color: "var(--danger-fg)" }}
        />
      )}
      <div className="flex flex-col">
        <span
          className="text-sm font-semibold"
          style={{ color: "var(--page-fg)" }}
        >
          {verified_ok ? "Integrity verified" : "Integrity broken"} ·{" "}
          {status.total_entries.toLocaleString()} entries
        </span>
        <span
          className="text-[11px]"
          style={{ color: "var(--page-fg-secondary)" }}
        >
          Chain head #{status.head_chain_index ?? "—"} · last checked{" "}
          {relativeAgo(status.last_verified_at)}
        </span>
      </div>
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={verifyNow}
        data-testid="audit-integrity-verify-button"
        className="ml-auto"
      >
        <RefreshCw className={`mr-1 h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} />
        {busy ? "Verifying…" : "Verify now"}
      </Button>
    </div>
  );
}

export default function AuditLogs() {
  const [searchParams, setSearchParams] = useSearchParams();
  // Honour ?entity_type=... in the URL so deep-links from the dashboard
  // "Removed Records" tile land with the case-events filter already applied.
  const initialEntity = (() => {
    const v = searchParams.get("entity_type");
    return ENTITY_TYPES.includes(v) ? v : "";
  })();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [entityType, setEntityType] = useState(initialEntity);
  // user-id → email lookup so the Actor column reads "mreyes@goodwinliving.org"
  // instead of "de1ae6bb-153b-49a2-…". Both HR admin and Compliance can read
  // /admin/users, so we fetch once on mount.
  const [actorEmails, setActorEmails] = useState({});

  const load = async (nextOffset, entity = entityType) => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, offset: nextOffset };
      if (entity) params.entity_type = entity;
      const { data } = await api.get("/audit-logs", { params });
      setRows(data);
      setHasMore(data.length === PAGE_SIZE);
      setOffset(nextOffset);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(0);
    // Build actor-id → email map once (HR admin + Compliance both have read).
    // Used purely for display in the Actor column; we degrade silently to the
    // raw UUID on failure (e.g. if the caller role lost access mid-session).
    api
      .get("/admin/users")
      .then((res) => {
        const map = {};
        for (const u of res.data || []) {
          if (u.id && u.email) map[u.id] = u.email;
        }
        setActorEmails(map);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const applyEntityFilter = (v) => {
    const next = v === "all" ? "" : v;
    setEntityType(next);
    // Keep URL in sync so a refresh preserves the filter and the back button
    // returns to the prior view.
    const nextParams = {};
    if (next) nextParams.entity_type = next;
    setSearchParams(nextParams, { replace: true });
    load(0, next);
  };

  const actionStyle = (a) => {
    // Phase 12.1 — minimal-glyph (dot + text) badges, AA-compliant in light + dark
    const map = {
      create: { dot: "#5EAB45", text: "var(--page-fg)" },
      update: { dot: "#3B82F6", text: "var(--page-fg)" },
      upload: { dot: "#D97706", text: "var(--page-fg)" },
      status_change: { dot: "#8B5CF6", text: "var(--page-fg)" },
      login: { dot: "#1E3A5F", text: "var(--page-fg)" },
    };
    return map[a] || { dot: "var(--page-fg-muted)", text: "var(--page-fg)" };
  };

  // Phase 2 (May 2026) — render the live audit enum codes as human-readable
  // labels. Backend enum stays unchanged (audit chain integrity); this is
  // display-only. Anything not in the map falls back to a title-cased version.
  const ACTION_LABELS = {
    // Generic
    create: "Created",
    update: "Updated",
    upload: "Uploaded",
    status_change: "Status changed",
    login: "Login",
    logout: "Logout",
    export: "Exported",
    auto_escalate: "Auto-escalated (SLA)",
    auto_assign: "Auto-assigned",
    reviewed: "Marked reviewed",
    virus_scan_rejected: "Upload blocked (virus scan)",
    // Auth / SSO / MFA
    sso_login: "SSO login",
    mfa_enrolled: "MFA enrolled",
    mfa_disabled: "MFA disabled",
    account_unlocked: "Account unlocked",
    // Storage
    storage_migration: "Storage migration",
    // AI
    ai_suggestion_generated: "AI suggestion generated",
    ai_suggestion_applied: "AI suggestion applied",
    ai_suggestion_dismissed: "AI suggestion dismissed",
    ai_closure_draft: "AI closure draft",
    ai_redaction_suggested: "AI redaction suggested",
    ai_redaction_applied: "AI redaction applied",
    // Self-service signup
    registration_requested: "Registration requested",
    registration_approved: "Registration approved",
    registration_rejected: "Registration rejected",
    password_reset_requested: "Password reset requested",
    password_reset_completed: "Password reset completed",
    profile_updated: "Profile updated",
    // Bootstrap (one-time, removed after deploy)
    bootstrap_admin: "Admin bootstrap (system)",
    // Phase 2 — user lifecycle
    user_create_by_admin: "User created by admin",
    user_password_reset_by_admin: "Password reset by admin",
    user_deactivate: "User deactivated",
    user_reactivate: "User reactivated",
    user_hard_delete: "User permanently removed",
    // Phase 2 — case lifecycle
    case_archive: "Case archived",
    case_archived: "Case archived (legacy)",
    case_restore: "Case restored",
    case_hard_delete: "Case permanently deleted",
    case_hard_deleted: "Case permanently deleted (legacy)",
    // Documents
    document_downloaded: "Document downloaded",
    document_access_attempt: "Document access attempt",
    document_access_denied: "Document access denied",
    document_hard_delete: "Document permanently deleted",
    service_line_update: "Service line updated",
    email_test_send: "Email test send (admin)",
    // PHI / scans
    phi_scan_completed: "PHI scan completed",
    phi_override_applied: "PHI override applied",
    access_review_exported: "Access review exported",
    // Legal hold
    legal_hold_set: "Legal hold set",
    legal_hold_cleared: "Legal hold cleared",
  };

  const humanAction = (a) => {
    if (ACTION_LABELS[a]) return ACTION_LABELS[a];
    if (!a) return "—";
    // Fallback: snake_case → "Snake Case"
    return a
      .toString()
      .split("_")
      .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w))
      .join(" ");
  };

  // Entity-type labels — keeps the audit table from showing raw collection
  // names like `case_template`, `notification_routing`, etc.
  const ENTITY_LABELS = {
    case: "Case",
    user: "User",
    user_admin: "User (admin action)",
    hr_partner: "HR Partner",
    team_member: "Team Member",
    document: "Document",
    settings: "Settings",
    notification_cadence: "Notification cadence",
    notification_routing: "Notification routing",
    workflow_transition: "Workflow transition",
    taxonomy: "Taxonomy",
    doc_category: "Document category",
    case_template: "Case template",
    service_line: "Service line",
    access_review: "Access review",
    audit_chain: "Audit chain",
    team_members_bulk_import: "Team members (bulk import)",
  };
  const humanEntity = (e) => ENTITY_LABELS[e] || humanAction(e);

  // For the entity-id column we try to surface a human reference:
  // - case → case_id_display from before/after if available, else short uuid
  // - user / user_admin / hr_partner → the email from before/after.target_email
  // - team_member → position_id / file_number from before/after
  // - everything else → short uuid or "—"
  const entityRef = (a) => {
    const e = a.entity_type;
    const before = a.before || {};
    const after = a.after || {};
    if (e === "case") {
      const cid =
        after.case_id_display ||
        before.case_id_display ||
        after.case_id ||
        before.case_id;
      if (cid) return cid;
    }
    if (e === "user" || e === "user_admin" || e === "hr_partner") {
      const email =
        after.target_email ||
        before.target_email ||
        after.email ||
        before.email;
      if (email) return email;
    }
    if (e === "team_member") {
      const pid =
        after.position_id ||
        before.position_id ||
        after.file_number ||
        before.file_number;
      if (pid !== undefined && pid !== null) return String(pid);
    }
    return a.entity_id ? `${a.entity_id.toString().slice(0, 8)}…` : "—";
  };

  return (
    <div data-testid="audit-logs-page" className="mx-auto max-w-7xl px-6 py-8">
      <div className="mb-4 flex flex-wrap items-stretch gap-3">
        <div className="flex-1 min-w-[280px]">
          <IntegrityBadge />
        </div>
        <BackupStatusBadge />
      </div>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1
            className="font-serif text-3xl font-semibold tracking-tight"
            style={{ letterSpacing: "-0.015em", color: "var(--page-fg)" }}
          >
            Audit trail
          </h1>
          <p className="mt-1 text-sm" style={{ color: "var(--page-fg-secondary)" }}>
            Append-only. Visible to HR and Compliance only.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={entityType || "all"} onValueChange={applyEntityFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="audit-entity-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All entities</SelectItem>
              {ENTITY_TYPES.map((e) => (
                <SelectItem key={e} value={e}>
                  {e}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            size="sm"
            disabled={offset === 0 || loading}
            onClick={() => load(Math.max(0, offset - PAGE_SIZE))}
            data-testid="audit-prev-page-button"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!hasMore || loading}
            onClick={() => load(offset + PAGE_SIZE)}
            data-testid="audit-next-page-button"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <Card className="brand-card overflow-hidden">
        <Table data-testid="audit-logs-table">
          <TableHeader>
            <TableRow
              className="brand-table-header hover:bg-transparent"
              style={{ borderBottom: "1px solid var(--border-subtle)" }}
            >
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>When</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>Action</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>Entity</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>Actor</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>IP</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>Device</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>Before</TableHead>
              <TableHead style={{ color: "var(--page-fg-secondary)" }}>After</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-[color:var(--page-fg-muted)]">
                  Loading…
                </TableCell>
              </TableRow>
            )}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={8} className="py-10 text-center text-sm text-[color:var(--page-fg-muted)]">
                  No audit entries.
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((a) => {
                const styleA = actionStyle(a.action);
                return (
                <TableRow
                  key={a.id}
                  data-testid={`audit-row-${a.id}`}
                  className="row-hoverable"
                  style={{ borderBottom: "1px solid var(--border-subtle)" }}
                >
                  <TableCell
                    className="whitespace-nowrap text-xs tabular-nums"
                    style={{ color: "var(--page-fg-secondary)" }}
                  >
                    {new Date(a.timestamp).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    <span
                      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] font-medium"
                      style={{
                        color: styleA.text,
                        backgroundColor: "var(--surface-2)",
                        boxShadow: "inset 0 0 0 1px var(--border-subtle)",
                      }}
                    >
                      <span
                        aria-hidden
                        className="inline-block h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: styleA.dot }}
                      />
                      {humanAction(a.action)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <span
                        className="text-sm font-medium"
                        style={{ color: "var(--page-fg)" }}
                      >
                        {humanEntity(a.entity_type)}
                      </span>
                      <span
                        className="font-mono text-[10px] tabular-nums"
                        style={{ color: "var(--page-fg-secondary)" }}
                        title={a.entity_id || ""}
                      >
                        {entityRef(a)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell
                    className="whitespace-nowrap text-[11px]"
                    style={{ color: "var(--page-fg-secondary)" }}
                    title={a.actor_user_id || ""}
                  >
                    {actorEmails[a.actor_user_id] || (a.actor_user_id ? `${a.actor_user_id.slice(0, 8)}…` : "—")}
                  </TableCell>
                  <TableCell
                    data-testid={`audit-ip-${a.id}`}
                    className="whitespace-nowrap font-mono text-[11px] tabular-nums"
                    style={{ color: "var(--page-fg-secondary)" }}
                  >
                    {a.ip_address || (
                      <span style={{ color: "var(--page-fg-muted)" }}>—</span>
                    )}
                  </TableCell>
                  <TableCell
                    data-testid={`audit-ua-${a.id}`}
                    title={a.user_agent || ""}
                    className="whitespace-nowrap text-[11px]"
                    style={{ color: "var(--page-fg-secondary)" }}
                  >
                    {shortUserAgent(a.user_agent)}
                  </TableCell>
                  <TableCell>
                    <pre
                      tabIndex={0}
                      role="region"
                      aria-label="Audit log before state"
                      className="max-h-32 max-w-[260px] overflow-auto text-[11px]"
                      style={{ color: "var(--page-fg-secondary)" }}
                    >
                      {a.before ? JSON.stringify(a.before, null, 2) : "—"}
                    </pre>
                  </TableCell>
                  <TableCell>
                    <pre
                      tabIndex={0}
                      role="region"
                      aria-label="Audit log after state"
                      className="max-h-32 max-w-[260px] overflow-auto text-[11px]"
                      style={{ color: "var(--page-fg-secondary)" }}
                    >
                      {a.after ? JSON.stringify(a.after, null, 2) : "—"}
                    </pre>
                  </TableCell>
                </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
